import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AddCleaningAreaSheet } from "@/pages/bids/AddCleaningAreaSheet";
import { ViewCleaningAreaSheet } from "@/pages/bids/ViewCleaningAreaSheet";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Trash2, FileText, Edit } from "lucide-react";

interface CleaningArea {
  id: string;
  name: string;
  squareFootage: number;
  frequency: string;
  productionRate: number;
  hoursPerDay: number;
  daysPerWeek: number;
  specialRequirements?: string;
  calculatedHours: number;
}

export function WorkLoadingTool() {
  const { toast } = useToast();
  const [cleaningAreas, setCleaningAreas] = useState<CleaningArea[]>([]);

  const [isAddAreaSheetOpen, setIsAddAreaSheetOpen] = useState(false);
  const [isViewAreaSheetOpen, setIsViewAreaSheetOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState<CleaningArea | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const totalWeeklyHours = cleaningAreas.reduce((total, area) => total + area.calculatedHours, 0);

  const totalSquareFootage = cleaningAreas.reduce((total, area) => total + area.squareFootage, 0);

  // Calculate average production rate only if there are areas
  const averageProductionRate =
    cleaningAreas.length > 0
      ? cleaningAreas.reduce((total, area) => total + area.productionRate, 0) / cleaningAreas.length
      : 0;

  const handleRemoveArea = (id: string) => {
    setCleaningAreas(cleaningAreas.filter((area) => area.id !== id));
    toast({
      title: "Area removed",
      description: "The cleaning area has been removed from the workloading calculator.",
    });
  };

  const handleAddArea = (newArea: any) => {
    setCleaningAreas([...cleaningAreas, newArea]);
    toast({
      title: "Area added",
      description: "New cleaning area has been added to the workloading calculator.",
    });
  };

  const handleViewArea = (area: CleaningArea) => {
    setSelectedArea(area);
    setIsViewAreaSheetOpen(true);
  };

  const handleEditArea = (area: CleaningArea) => {
    setSelectedArea(area);
    setIsEditMode(true);
    setIsAddAreaSheetOpen(true);
  };

  const handleUpdateArea = (updatedArea: CleaningArea) => {
    setCleaningAreas(
      cleaningAreas.map((area) => (area.id === updatedArea.id ? updatedArea : area))
    );
    setIsEditMode(false);
    toast({
      title: "Area updated",
      description: "The cleaning area has been updated successfully.",
    });
  };

  return (
    <div className="bg-white p-6">
      {isAddAreaSheetOpen && (
        <AddCleaningAreaSheet
          isOpen={isAddAreaSheetOpen}
          onClose={() => {
            setIsAddAreaSheetOpen(false);
            setIsEditMode(false);
          }}
          onAdd={isEditMode && selectedArea ? handleUpdateArea : handleAddArea}
          initialData={isEditMode && selectedArea ? selectedArea : undefined}
          isEditMode={isEditMode}
        />
      )}

      {isViewAreaSheetOpen && selectedArea && (
        <ViewCleaningAreaSheet
          isOpen={isViewAreaSheetOpen}
          onClose={() => setIsViewAreaSheetOpen(false)}
          area={selectedArea}
          onEdit={() => {
            setIsViewAreaSheetOpen(false);
            handleEditArea(selectedArea);
          }}
        />
      )}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Workloading Calculator</h2>
          <p className="text-gray-500">Calculate labor hours needed for cleaning areas</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_350px]">
        <div className="space-y-6">
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-md font-medium">Cleaning Areas</h3>
              <Button onClick={() => setIsAddAreaSheetOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" /> Add Cleaning Area
              </Button>
            </div>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Area</TableHead>
                    <TableHead>Square Footage</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Production Rate</TableHead>
                    <TableHead>Hours/Day</TableHead>
                    <TableHead>Days/Week</TableHead>
                    <TableHead>Weekly Hours</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cleaningAreas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="py-4 text-center text-gray-500">
                        No cleaning areas added yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    cleaningAreas.map((area) => (
                      <TableRow key={area.id}>
                        <TableCell className="font-medium">{area.name}</TableCell>
                        <TableCell>{area.squareFootage.toLocaleString()}</TableCell>
                        <TableCell>
                          {area.frequency.charAt(0).toUpperCase() + area.frequency.slice(1)}
                        </TableCell>
                        <TableCell>{area.productionRate.toLocaleString()}</TableCell>
                        <TableCell>{area.hoursPerDay}</TableCell>
                        <TableCell>{area.daysPerWeek}</TableCell>
                        <TableCell className="font-semibold">{area.calculatedHours}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewArea(area)}
                              title="View Details"
                            >
                              <FileText className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditArea(area)}
                              title="Edit Area"
                            >
                              <Edit className="h-4 w-4 text-gray-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveArea(area.id)}
                              title="Remove Area"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">Workloading Summary</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Square Footage:</span>
                <span className="text-lg font-semibold">
                  {totalSquareFootage > 0 ? totalSquareFootage.toLocaleString() : "0"} sq ft
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Average Production Rate:</span>
                <span className="text-lg font-semibold">
                  {averageProductionRate > 0 ? averageProductionRate.toFixed(0) : "0"} sq ft/hr
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Total Weekly Hours:</span>
                <span className="text-xl font-bold text-blue-600">
                  {totalWeeklyHours > 0 ? totalWeeklyHours.toFixed(1) : "0.0"} hours
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">FTEs Required:</span>
                <span className="text-xl font-bold text-blue-600">
                  {totalWeeklyHours > 0 ? (totalWeeklyHours / 40).toFixed(1) : "0.0"}
                </span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">Staffing Breakdown</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Full-time Staff (40 hrs):</span>
                <span className="font-semibold">
                  {totalWeeklyHours > 0 ? Math.floor(totalWeeklyHours / 40) : 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Part-time Hours:</span>
                <span className="font-semibold">
                  {totalWeeklyHours > 0 ? (totalWeeklyHours % 40).toFixed(1) : "0.0"} hrs
                </span>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Daily Hours Needed:</span>
                <span className="font-semibold">
                  {totalWeeklyHours > 0 ? (totalWeeklyHours / 5).toFixed(1) : "0.0"} hrs/day
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
